import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';

import { placesFetchedFields } from '@/constants';
import { GlobalErrorType, useErrorStore } from '@/store/ErrorStore';
import { sendSynapseAnalytics } from '@/utils/analytics';
import { getAirQuality, RouteAirQuality } from '@/utils/cerc';
import { getDirections, getGoogleMapLink } from '@/utils/maps';
import { formatFriendlyDate, parseFriendlyDate, updateUrlParam } from '@/utils/url';

export type TravelType = 'walk' | 'bike';
export type LocationOffset = 'start' | 'end';

export type LocationSearch = {
  offset: LocationOffset;
  suggestions: google.maps.places.AutocompleteSuggestion[];
  filteredOutCount?: number;
  totalCount?: number;
};

export type AQRoute = google.maps.DirectionsRoute & {
  airQuality?: RouteAirQuality;
};

export type AQDirections = Omit<google.maps.DirectionsResult, 'routes'> & {
  routes: AQRoute[];
};

export interface SearchState {
  travelTime?: Date;
  setTravelTime: (travelTime?: Date) => void;
  travelType: TravelType;
  setTravelType: (travelType: TravelType) => void;
  locationSearch?: LocationSearch;
  setLocationSearch: (locationSearch?: LocationSearch) => void;
  locationStart?: google.maps.places.Place;
  setLocationStart: (locationStart?: google.maps.places.Place) => void;
  locationEnd?: google.maps.places.Place;
  setLocationEnd: (locationEnd?: google.maps.places.Place) => void;
  initializeLocations: (places: google.maps.PlacesLibrary) => void;
  searching: boolean;
  setSearching: (searching: boolean) => void;
  searchIndex?: number;
  setSearchIndex: (searchIndex?: number) => void;
  directions?: AQDirections;
  clearDirections: () => void;
  selectedRoute?: AQRoute;
  setSelectedRoute: (selectedRoute?: AQRoute) => void;
  highlightedRoute?: AQRoute;
  setHighlightedRoute: (highlightedRoute?: AQRoute) => void;
  googleMapLink?: string;
  activeStep?: number;
  setActiveStep: (activeStep: number) => void;
  syncWithUrlParams: () => void;
  error?: string;
  setError: (error?: string) => void;
}

const searchParams = new URLSearchParams(window.location.search);
const travelTimeParam = searchParams.get('time');
const travelTypeParam = searchParams.get('type') as TravelType | null;
const locationStartParam = searchParams.get('start');
const locationEndParam = searchParams.get('end');

export const useSearchStore = create<SearchState>()(
  subscribeWithSelector((set, get, api) => {
    window.addEventListener('popstate', () => get().syncWithUrlParams());

    api.subscribe(
      (state) => ({
        locationStart: state?.locationStart,
        locationEnd: state?.locationEnd,
        travelType: state?.travelType,
        travelTime: state?.travelTime,
        selectedRoute: state?.selectedRoute,
      }),
      async ({ locationStart, locationEnd, travelType, travelTime }) => {
        set({ locationSearch: undefined, error: undefined });
        useErrorStore.getState().clearGlobalError();

        if (!locationStart || !locationEnd) return;

        if (locationStart.id === locationEnd.id) {
          set({ directions: undefined, searching: false });
          return;
        }

        set({ searching: true });

        const directions: AQDirections | undefined = await getDirections(
          locationStart,
          locationEnd,
          travelType === 'bike' ? google.maps.TravelMode.BICYCLING : google.maps.TravelMode.WALKING,
        );

        // will be resolved in the Sentry PR
        if (!directions || !directions.routes) {
          set({ searching: false });
          useErrorStore.getState().setGlobalError(GlobalErrorType.SERVICE_OUTAGE);
          return;
        }

        const airQuality = await getAirQuality(travelTime, directions.routes);

        if (!airQuality) {
          set({ searching: false });
          useErrorStore.getState().setGlobalError(GlobalErrorType.SERVICE_OUTAGE);
          return;
        }

        airQuality.forEach((aq) => {
          const googleRoute = directions.routes.find((r) => r.overview_polyline === aq.polyline_id);
          if (!googleRoute) {
            throw Error('Google route not found on CERC API response');
          }
          googleRoute.airQuality = aq;
        });

        directions.routes = [...directions.routes].sort((a, b) => {
          const aHasData = a.airQuality?.in_modelled_area === 'Y' ? 0 : 1;
          const bHasData = b.airQuality?.in_modelled_area === 'Y' ? 0 : 1;
          if (aHasData !== bHasData) return aHasData - bHasData;
          return (a.airQuality?.route_ranking || 0) - (b.airQuality?.route_ranking || 0);
        });

        const isInitial = !get().directions;

        set({ directions, searching: false, locationSearch: undefined });

        const searchParams = new URLSearchParams(window.location.search);
        const route = searchParams.get('route');
        const selectedRoute = directions.routes.find((r) => r.airQuality?.route_ranking === Number(route));

        if (route) {
          set({ selectedRoute, activeStep: 0 });
        }

        const highlightedRoute = selectedRoute ?? directions.routes[0];
        const googleMapLink = getGoogleMapLink(
          highlightedRoute,
          travelType === 'bike' ? google.maps.TravelMode.BICYCLING : google.maps.TravelMode.WALKING,
        );

        set({ highlightedRoute, googleMapLink });

        if (isInitial) {
          sendSynapseAnalytics('Initial Search');
        }

        if (selectedRoute) {
          sendSynapseAnalytics('Start Navigation');
        }
      },
      {
        equalityFn: (a, b) =>
          a.locationStart?.id === b.locationStart?.id &&
          a.locationEnd?.id === b.locationEnd?.id &&
          a.travelType === b.travelType &&
          a.travelTime?.getTime() === b.travelTime?.getTime() &&
          a.selectedRoute?.airQuality?.route_ranking === b.selectedRoute?.airQuality?.route_ranking,
      },
    );

    api.subscribe(
      (state) => ({
        locationStart: state?.locationStart,
        locationEnd: state?.locationEnd,
        error: state?.error,
      }),
      ({ locationStart, locationEnd, error }) => {
        if (!error && locationStart && locationEnd && locationStart?.id === locationEnd?.id) {
          set({ error: 'Your start and end locations cannot be the same.' });
        }
      },
    );

    return {
      travelTime: travelTimeParam ? parseFriendlyDate(travelTimeParam) : undefined,
      setTravelTime: (travelTime) => {
        set({ travelTime, selectedRoute: undefined, highlightedRoute: undefined });
        updateUrlParam('time', formatFriendlyDate(travelTime));
        sendSynapseAnalytics('Change Datetime');
      },
      travelType: travelTypeParam ? travelTypeParam : 'walk',
      setTravelType: (travelType) => {
        set({ travelType });
        updateUrlParam('type', travelType);
        sendSynapseAnalytics('Change Journey Type');
      },
      setLocationSearch: (locationSearch) => set({ locationSearch }),
      setLocationStart: (locationStart) => {
        set({ locationStart });
        updateUrlParam('start', locationStart ? locationStart.id : undefined);
        if (locationStart) sendSynapseAnalytics('Change From');
      },
      setLocationEnd: (locationEnd) => {
        set({ locationEnd });
        updateUrlParam('end', locationEnd ? locationEnd.id : undefined);
        if (locationEnd) sendSynapseAnalytics('Change To');
      },
      initializeLocations: (places) => {
        if (locationStartParam) {
          const locationStart = new places.Place({ id: locationStartParam });
          locationStart
            .fetchFields({ fields: ['displayName', 'addressComponents'] })
            .then(() => set({ locationStart }));
        }
        if (locationEndParam) {
          const locationEnd = new places.Place({ id: locationEndParam });
          locationEnd.fetchFields({ fields: ['displayName', 'addressComponents'] }).then(() => set({ locationEnd }));
        }
      },
      searching: false,
      setSearching: (searching) => {
        set({ searching });
      },
      setSearchIndex: (searchIndex) => set({ searchIndex }),
      clearDirections: () => set({ directions: undefined }),
      setSelectedRoute: (selectedRoute) => {
        set({ selectedRoute, activeStep: 0 });
        updateUrlParam('route', selectedRoute?.airQuality?.route_ranking?.toString() || undefined);
        sendSynapseAnalytics(selectedRoute ? 'Select Route' : 'Unselect Route');
      },
      setHighlightedRoute: (highlightedRoute) => {
        const { travelType } = get();
        const googleMapLink = getGoogleMapLink(
          highlightedRoute,
          travelType === 'bike' ? google.maps.TravelMode.BICYCLING : google.maps.TravelMode.WALKING,
        );
        set({ highlightedRoute, googleMapLink });
      },
      setActiveStep: (activeStep) => set({ activeStep }),
      syncWithUrlParams: () => {
        const params = new URLSearchParams(window.location.search);
        const timeParam = params.get('time');
        const typeParam = params.get('type');
        const startParam = params.get('start');
        const endParam = params.get('end');
        const routeParam = params.get('route');

        const state = get();

        if (timeParam !== formatFriendlyDate(state.travelTime)) {
          const newTravelTime = timeParam ? parseFriendlyDate(timeParam) : undefined;
          set({ travelTime: newTravelTime });
        }

        if (typeParam !== state.travelType) {
          set({ travelType: (typeParam as TravelType) || 'walk' });
        }

        const placesLib =
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (window as any).google?.maps?.places as google.maps.PlacesLibrary;

        if (startParam && (!state.locationStart || state.locationStart.id !== startParam)) {
          const locationStart = new placesLib.Place({ id: startParam });
          locationStart.fetchFields({ fields: placesFetchedFields }).then(() => set({ locationStart }));
        } else if (!startParam && state.locationStart) {
          set({ locationStart: undefined, selectedRoute: undefined });
        }

        if (endParam && (!state.locationEnd || state.locationEnd.id !== endParam)) {
          const locationEnd = new placesLib.Place({ id: endParam });
          locationEnd.fetchFields({ fields: placesFetchedFields }).then(() => set({ locationEnd }));
        } else if (!endParam && state.locationEnd) {
          set({ locationEnd: undefined, selectedRoute: undefined });
        }

        if (routeParam !== state.selectedRoute?.airQuality?.route_ranking?.toString()) {
          if (!routeParam) {
            set({ selectedRoute: undefined });
          } else {
            set({ selectedRoute: state.directions?.routes[Number(routeParam)], activeStep: 0 });
          }
        }
      },
      setError: (error) => set({ error }),
    };
  }),
);

export const sentryState = () => {
  const state = useSearchStore.getState();
  return {
    travelTime: state.travelTime,
    travelType: state.travelType,
    locationStartId: state.locationStart?.id,
    locationStartName: state.locationStart?.displayName,
    locationEndId: state.locationEnd?.id,
    locationEndName: state.locationEnd?.displayName,
    selectedRouteRanking: state.selectedRoute?.airQuality?.route_ranking,
    directions: JSON.stringify(
      state.directions?.routes.map((route) => ({
        routeRanking: route.airQuality?.route_ranking,
        distance: route.legs[0].distance?.text,
        duration: route.legs[0].duration?.text,
      })),
      null,
      '  ',
    ),
    googleMapLink: state.googleMapLink,
  };
};
